/**
 * Pure policy for personalised invite RSVP links.
 * Mirrors RSVPPage behaviour and guest-create rules in backend/firestore.rules.
 */

import type { ResolvedInviteLinkParams } from '@/lib/personal-invite-links';

export interface InviteRsvpSubmitFields {
    invitedBy?: string;
    inviteLinkId?: string;
    inviterPaysFor?: boolean;
    paid?: boolean;
}

export interface RsvpInviterPaysClaim {
    invitedBy?: string;
    inviterPaysFor?: boolean;
    paid?: boolean;
    inviteLinkId?: string;
}

export interface RsvpLinkDocForValidation {
    invitedBy?: string;
    inviterPaysFor?: boolean;
}

/**
 * Effective inviter id for ticket scope and RSVP attribution.
 * Server-loaded link doc wins over URL invitedBy once fetch completes.
 */
export function resolveEffectiveInvitedBy(
    resolvedInviteLink: ResolvedInviteLinkParams | null | undefined,
    invitedByParam: string | null | undefined,
): string | null {
    if (resolvedInviteLink === undefined) {
        return invitedByParam?.trim() || null;
    }
    return (resolvedInviteLink?.invitedBy || invitedByParam)?.trim() || null;
}

/**
 * Host-pays only when invite link doc loaded and explicitly grants it.
 * URL inviterPaysFor query param is never consulted.
 */
export function computeInviterPaysForGuest(
    invitedBy: string | null | undefined,
    resolvedInviteLink: ResolvedInviteLinkParams | null | undefined,
): boolean {
    return Boolean(
        invitedBy?.trim()
        && resolvedInviteLink
        && resolvedInviteLink.inviterPaysFor === true,
    );
}

/**
 * Fields written onto a new RSVP from invite link state (RSVPPage submit path).
 */
export function buildInviteFieldsForRsvpSubmit(
    invitedBy: string | null | undefined,
    resolvedInviteLink: ResolvedInviteLinkParams | null | undefined,
): InviteRsvpSubmitFields {
    const fields: InviteRsvpSubmitFields = {};
    const trimmedInvitedBy = invitedBy?.trim();

    if (trimmedInvitedBy) {
        fields.invitedBy = trimmedInvitedBy;
    }
    if (resolvedInviteLink?.inviteLinkId) {
        fields.inviteLinkId = resolvedInviteLink.inviteLinkId;
        fields.inviterPaysFor = resolvedInviteLink.inviterPaysFor;
    }
    fields.paid = computeInviterPaysForGuest(trimmedInvitedBy, resolvedInviteLink);

    return fields;
}

function claimsInviterPays(claim: RsvpInviterPaysClaim): boolean {
    const invitedBy = typeof claim.invitedBy === 'string' ? claim.invitedBy.trim() : '';
    if (!invitedBy) {
        return false;
    }
    return claim.inviterPaysFor === true || claim.paid === true;
}

/**
 * Guest (unauthenticated) RSVP create: host-pays must match a stored rsvpLinks document.
 * Authenticated creates are allowed through (hosts adding guests from dashboard).
 */
export function guestRsvpInviterPaysClaimIsValid(
    claim: RsvpInviterPaysClaim,
    linkDoc: RsvpLinkDocForValidation | null | undefined,
    options: { isAuthenticated: boolean },
): boolean {
    if (!claimsInviterPays(claim)) {
        return true;
    }
    if (options.isAuthenticated) {
        return true;
    }

    const inviteLinkId = typeof claim.inviteLinkId === 'string' ? claim.inviteLinkId.trim() : '';
    if (!inviteLinkId || !linkDoc) {
        return false;
    }
    if (linkDoc.inviterPaysFor !== true) {
        return false;
    }

    const linkInvitedBy = typeof linkDoc.invitedBy === 'string' ? linkDoc.invitedBy.trim() : '';
    const claimInvitedBy = typeof claim.invitedBy === 'string' ? claim.invitedBy.trim() : '';
    return linkInvitedBy.length > 0 && linkInvitedBy === claimInvitedBy;
}
